import { Link, useSearchParams, useNavigate, useParams } from "react-router-dom";
import { useShadowRunPacket } from "../hooks/useShadowRunPacket";
import { SeverityBadge } from "../components/SeverityBadge/SeverityBadge";
import { ComparisonTable } from "../components/ComparisonTable/ComparisonTable";
import { formatNZD } from "../utils/formatters";
import type { PacketDetail } from "../types/shadow-run";
import styles from "./PacketDetailScreen.module.css";

function PacketDetailContent({
  batchId,
  packetId,
  orgCategory,
  orgId,
}: {
  batchId: string;
  packetId: string;
  orgCategory: string;
  orgId: string;
}) {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useShadowRunPacket(
    orgCategory,
    orgId,
    batchId,
    packetId,
  );

  if (isLoading) {
    return <p className={styles.message}>Loading packet details…</p>;
  }

  if (isError || !data) {
    return (
      <p className={styles.message}>
        Unable to load packet details. Please try again.
      </p>
    );
  }

  const packet: PacketDetail = data;
  const netDifference = packet.pacific_net - packet.fsc_net;
  const hasVariance = netDifference !== 0;

  function handleGraphClick() {
    navigate(
      `/shadow-runs/${batchId}/packets/${packetId}/graph?org_category=${encodeURIComponent(orgCategory)}&org_id=${encodeURIComponent(orgId)}`,
    );
  }

  return (
    <>
      {/* ── Header ── */}
      <div className={styles.header}>
        <h2>Packet Detail</h2>
        <SeverityBadge tier={packet.severity_tier} />
        <span className={styles.packetId}>{packet.packet_id}</span>
      </div>

      {/* ── Hero: side-by-side summary + net difference ── */}
      <div className={styles.hero}>
        <div className={styles.heroComparison}>
          <div className={styles.heroSide}>
            <span className={styles.heroSideLabel}>FSC Gross</span>
            <span className={styles.heroSideValue}>
              {formatNZD(packet.fsc_gross)}
            </span>
          </div>
          <span className={styles.heroVs}>vs</span>
          <div className={styles.heroSide}>
            <span className={styles.heroSideLabel}>Pacific Gross</span>
            <span className={styles.heroSideValue}>
              {formatNZD(packet.pacific_gross)}
            </span>
          </div>
        </div>
        <div className={styles.heroMetric}>
          <span
            className={
              hasVariance ? styles.heroValue : styles.heroValueMatch
            }
          >
            {formatNZD(netDifference)}
          </span>
          <span className={styles.heroLabel}>Net Difference</span>
        </div>
      </div>

      {/* ── Field Comparison ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Field Comparison</h3>
        <ComparisonTable
          rows={packet.comparison_rows}
          varianceDetails={packet.variance_details}
        />
      </section>

      {/* ── AI Analysis (conditional) ── */}
      {packet.ai_analysis && (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>AI Analysis</h3>
          <div className={styles.aiAnalysis}>{packet.ai_analysis}</div>
        </section>
      )}

      {/* ── CTA ── */}
      <button
        type="button"
        className={styles.ctaButton}
        onClick={handleGraphClick}
        data-testid="graph-cta"
      >
        Investigate in Calculation Graph
      </button>
    </>
  );
}

export function PacketDetailScreen() {
  const { batchId, packetId } = useParams<{
    batchId: string;
    packetId: string;
  }>();
  const [searchParams] = useSearchParams();
  const orgCategory = searchParams.get("org_category");
  const orgId = searchParams.get("org_id");

  if (!batchId || !packetId) {
    return (
      <div className={styles.page}>
        <p className={styles.message}>
          Missing batch or packet ID. Please navigate from the packet list.
        </p>
      </div>
    );
  }

  if (!orgCategory || !orgId) {
    return (
      <div className={styles.page}>
        <p className={styles.message}>
          Missing organisation parameters. Please navigate from the packet list.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/shadow-runs" className={styles.breadcrumbLink}>
          Shadow Runs
        </Link>
        <span className={styles.breadcrumbSep}>/</span>
        <Link
          to={`/shadow-runs/${batchId}?org_category=${encodeURIComponent(orgCategory)}&org_id=${encodeURIComponent(orgId)}`}
          className={styles.breadcrumbLink}
        >
          {batchId}
        </Link>
        <span className={styles.breadcrumbSep}>/</span>
        <Link
          to={`/shadow-runs/${batchId}/packets?org_category=${encodeURIComponent(orgCategory)}&org_id=${encodeURIComponent(orgId)}`}
          className={styles.breadcrumbLink}
        >
          Packets
        </Link>
        <span className={styles.breadcrumbSep}>/</span>
        <span className={styles.breadcrumbCurrent}>{packetId}</span>
      </nav>

      <PacketDetailContent
        batchId={batchId}
        packetId={packetId}
        orgCategory={orgCategory}
        orgId={orgId}
      />
    </div>
  );
}
